import { useState, useEffect, useRef } from 'react';
import { useAuth, useBusinessUnit } from '@/App';
import { expenses as expensesApi, agents as agentsApi } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Plus, Search, Trash2, Receipt, Pencil, Upload, FileImage, X, Eye, Download, FileSpreadsheet, ArrowRight, CheckSquare, Square, RefreshCw, Mail, CalendarRange, FileOutput, ChevronDown, ChevronRight, Calendar, CheckCircle, XCircle, Clock, Filter, Building2, Phone, DollarSign } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('token');

export default function Expenses() {
  const { user } = useAuth();
  const { businessUnit } = useBusinessUnit();
  const isAgent = user?.role === 'agent';
  const [expenses, setExpenses] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [viewingReceipt, setViewingReceipt] = useState(null);
  const receiptInputRef = useRef(null);
  
  // Import state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importStep, setImportStep] = useState('upload'); // upload, map, preview
  const [importFile, setImportFile] = useState(null);
  const [importFiles, setImportFiles] = useState([]); // Multiple files support
  const [importData, setImportData] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [importLoading, setImportLoading] = useState(false);
  const [matchedAgentsPreview, setMatchedAgentsPreview] = useState({}); // For confidence indicator
  const [importSessionId, setImportSessionId] = useState(null); // Session ID for full import
  const [importTotalRows, setImportTotalRows] = useState(0); // Total rows in file
  
  // Bulk selection state
  const [selectedExpenses, setSelectedExpenses] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Expense statement state
  const [showStatementModal, setShowStatementModal] = useState(false);
  const [statementForm, setStatementForm] = useState({
    agent_id: '',
    start_date: '',
    end_date: new Date().toISOString().split('T')[0]
  });
  const [generatingStatement, setGeneratingStatement] = useState(false);
  
  // Monthly summary state (for agents)
  const [expandedMonths, setExpandedMonths] = useState({});
  const [generatingMonthlyPdf, setGeneratingMonthlyPdf] = useState(null);
  
  // Export state (approval removed)
  const [exportLoading, setExportLoading] = useState(false);
  
  // Accounting categories state
  const [accountingCategories, setAccountingCategories] = useState([]);
  const [bulkCategorizingLoading, setBulkCategorizingLoading] = useState(false);
  
  // Vendors state for auto-fill
  const [vendors, setVendors] = useState([]);
  const [showQuickAddVendor, setShowQuickAddVendor] = useState(false);
  const [quickVendorForm, setQuickVendorForm] = useState({
    name: '',
    email: '',
    phone: '',
    vendor_type: 'supplier',
    payment_terms: 'net30',
    default_expense_category: ''
  });
  const [savingQuickVendor, setSavingQuickVendor] = useState(false);
  
  // Vendor Quick View state
  const [vendorStats, setVendorStats] = useState({});
  const [loadingVendorStats, setLoadingVendorStats] = useState({});
  
  const [formData, setFormData] = useState({
    agent_id: '',
    co_agent_id: '',
    vendor: '',
    description: '',
    unit_price: '0',
    quantity: '1',
    ancillary: '0',
    amount: '',
    tax: '0',
    expense_date: new Date().toISOString().split('T')[0],
    payment_date: '',
    receipt: null,
    receipt_filename: '',
    accounting_account_id: ''
  });

  // Calculate 5% default tax when amount changes
  const calculateDefaultTax = (unitPrice, quantity, ancillary) => {
    const amount = (parseFloat(unitPrice) || 0) * (parseFloat(quantity) || 1) + (parseFloat(ancillary) || 0);
    return (amount * 0.05).toFixed(2); // 5% tax
  };

  // Auto-update tax when price fields change (only for new expenses)
  const handlePriceChange = (field, value) => {
    setFormData(prev => {
      const newFormData = { ...prev, [field]: value };
      
      // Only auto-calculate tax for new expenses, not when editing
      if (!editingExpense) {
        const unitPrice = field === 'unit_price' ? value : newFormData.unit_price;
        const quantity = field === 'quantity' ? value : newFormData.quantity;
        const ancillary = field === 'ancillary' ? value : newFormData.ancillary;
        
        const amount = (parseFloat(unitPrice) || 0) * (parseFloat(quantity) || 1) + (parseFloat(ancillary) || 0);
        newFormData.tax = (amount * 0.05).toFixed(2); // 5% tax
      }
      
      return newFormData;
    });
  };

  // Handle vendor selection - auto-fill category and calculate due date
  const handleVendorSelect = (vendorId) => {
    if (!vendorId || vendorId === 'manual') {
      setFormData(prev => ({ ...prev, vendor: '' }));
      return;
    }
    
    const selectedVendor = vendors.find(v => v.id === vendorId);
    if (!selectedVendor) return;
    
    const updates = {
      vendor: selectedVendor.name
    };
    
    // Auto-fill category if vendor has default
    if (selectedVendor.default_expense_category && !formData.accounting_account_id) {
      updates.accounting_account_id = selectedVendor.default_expense_category;
      toast.info(`Category auto-filled: ${selectedVendor.default_expense_category_name || 'Default category'}`);
    }
    
    // Calculate payment due date based on payment terms
    if (selectedVendor.payment_terms && !formData.payment_date) {
      const expenseDate = formData.expense_date ? new Date(formData.expense_date) : new Date();
      let daysToAdd = 0;
      
      switch (selectedVendor.payment_terms) {
        case 'immediate': daysToAdd = 0; break;
        case 'net15': daysToAdd = 15; break;
        case 'net30': daysToAdd = 30; break;
        case 'net45': daysToAdd = 45; break;
        case 'net60': daysToAdd = 60; break;
        case 'net90': daysToAdd = 90; break;
        default: daysToAdd = 30;
      }
      
      if (daysToAdd > 0) {
        const dueDate = new Date(expenseDate);
        dueDate.setDate(dueDate.getDate() + daysToAdd);
        updates.payment_date = dueDate.toISOString().split('T')[0];
        toast.info(`Due date set: ${updates.payment_date} (${selectedVendor.payment_terms.replace('net', 'Net ')})`);
      }
    }
    
    setFormData(prev => ({ ...prev, ...updates }));
  };

  // Quick Add Vendor functions
  const resetQuickVendorForm = () => {
    setQuickVendorForm({
      name: '',
      email: '',
      phone: '',
      vendor_type: 'supplier',
      payment_terms: 'net30',
      default_expense_category: ''
    });
  };

  const openQuickAddVendor = () => {
    resetQuickVendorForm();
    setShowQuickAddVendor(true);
  };

  const saveQuickVendor = async () => {
    if (!quickVendorForm.name.trim()) {
      toast.error('Vendor name is required');
      return;
    }

    setSavingQuickVendor(true);
    const token = getToken();
    
    try {
      const response = await axios.post(`${API}/api/vendors`, {
        name: quickVendorForm.name,
        email: quickVendorForm.email,
        phone: quickVendorForm.phone,
        vendor_type: quickVendorForm.vendor_type,
        payment_terms: quickVendorForm.payment_terms,
        default_expense_category: quickVendorForm.default_expense_category || null,
        is_active: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const newVendor = response.data;
      toast.success(`Vendor "${newVendor.name}" created successfully`);
      
      // Reload vendors list
      await loadVendors();
      
      // Auto-select the new vendor and trigger auto-fill
      setShowQuickAddVendor(false);
      
      // Small delay to ensure vendors list is updated
      setTimeout(() => {
        handleVendorSelect(newVendor.id);
      }, 100);
      
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create vendor');
    } finally {
      setSavingQuickVendor(false);
    }
  };

  // Fetch vendor stats for Quick View popover
  const fetchVendorStats = async (vendorName) => {
    if (!vendorName || vendorStats[vendorName] || loadingVendorStats[vendorName]) return;
    
    // Find vendor by name
    const vendor = vendors.find(v => v.name === vendorName);
    if (!vendor) {
      // For vendors not in our list, calculate from expenses
      const vendorExpenses = expenses.filter(e => e.vendor === vendorName);
      const totalSpent = vendorExpenses.reduce((sum, e) => sum + (e.total_amount || e.amount + e.tax), 0);
      setVendorStats(prev => ({
        ...prev,
        [vendorName]: {
          name: vendorName,
          total_spent: totalSpent,
          expense_count: vendorExpenses.length,
          is_registered: false
        }
      }));
      return;
    }
    
    setLoadingVendorStats(prev => ({ ...prev, [vendorName]: true }));
    try {
      const token = getToken();
      const response = await axios.get(`${API}/api/vendors/${vendor.id}/transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendorStats(prev => ({
        ...prev,
        [vendorName]: {
          ...vendor,
          ...response.data,
          is_registered: true
        }
      }));
    } catch (err) {
      // Fallback to expense-based calculation
      const vendorExpenses = expenses.filter(e => e.vendor === vendorName);
      const totalSpent = vendorExpenses.reduce((sum, e) => sum + (e.total_amount || e.amount + e.tax), 0);
      setVendorStats(prev => ({
        ...prev,
        [vendorName]: {
          ...vendor,
          total_spent: totalSpent,
          expense_count: vendorExpenses.length,
          is_registered: true
        }
      }));
    } finally {
      setLoadingVendorStats(prev => ({ ...prev, [vendorName]: false }));
    }
  };

  useEffect(() => {
    loadData();
    loadAccountingCategories();
    loadVendors();
  }, [businessUnit]); // Reload when business unit changes

  const loadVendors = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/api/vendors?is_active=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendors(response.data || []);
    } catch (err) {
      console.error('Failed to load vendors', err);
    }
  };

  const loadAccountingCategories = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/api/expenses/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccountingCategories(response.data.categories || []);
    } catch (err) {
      console.error('Failed to load accounting categories', err);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [expRes, agentsRes] = await Promise.all([
        expensesApi.list(),
        agentsApi.list()
      ]);
      
      // Filter expenses for agents to only show their own
      let expenseData = expRes.data;
      if (isAgent && user?.agent_id) {
        expenseData = expenseData.filter(e => e.agent_id === user.agent_id);
      }
      
      setExpenses(expenseData);
      setAgents(agentsRes.data);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkCategorize = async () => {
    setBulkCategorizingLoading(true);
    try {
      const token = getToken();
      const response = await axios.post(`${API}/api/expenses/bulk-categorize`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${response.data.categorized_count} expenses auto-categorized`);
      loadData();
    } catch (err) {
      toast.error('Failed to auto-categorize expenses');
    } finally {
      setBulkCategorizingLoading(false);
    }
  };

  // Export functions
  const handleExport = async (format) => {
    setExportLoading(true);
    const token = getToken();
    const prefix = businessUnit === 'insurance' ? 'insurance/' : '';
    
    try {
      const params = new URLSearchParams();
      
      const response = await axios.get(
        `${API}/api/${prefix}expenses/export/${format}?${params.toString()}`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `expenses_export_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : 'csv'}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported to ${format.toUpperCase()}`);
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setExportLoading(false);
    }
  };

  const resetForm = () => {
    // Default 5% tax on $0 amount = $0
    setFormData({
      agent_id: '',
      co_agent_id: '',
      vendor: '',
      description: '',
      unit_price: '0',
      quantity: '1',
      ancillary: '0',
      amount: '',
      tax: '0.00', // Will be auto-calculated as 5% when amounts change
      expense_date: new Date().toISOString().split('T')[0],
      payment_date: '',
      receipt: null,
      receipt_filename: '',
      accounting_account_id: ''
    });
    setEditingExpense(null);
    setSelectedExpenses([]); // Clear selections
    setSelectAll(false);
    if (receiptInputRef.current) {
      receiptInputRef.current.value = '';
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      agent_id: expense.agent_id,
      co_agent_id: expense.co_agent_id || '',
      vendor: expense.vendor || '',
      description: expense.description,
      unit_price: (expense.unit_price || 0).toString(),
      quantity: (expense.quantity || 1).toString(),
      ancillary: (expense.ancillary || 0).toString(),
      amount: expense.amount.toString(),
      tax: expense.tax.toString(),
      expense_date: expense.expense_date,
      payment_date: expense.payment_date || '',
      receipt: expense.receipt || null,
      receipt_filename: expense.receipt_filename || '',
      accounting_account_id: expense.accounting_account_id || ''
    });
    setShowModal(true);
  };

  const handleReceiptUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload an image (JPG, PNG, GIF, WebP) or PDF file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onload = (event) => {
      setFormData({
        ...formData,
        receipt: event.target?.result,
        receipt_filename: file.name
      });
      toast.success('Receipt uploaded');
    };
    reader.onerror = () => {
      toast.error('Failed to read file');
    };
    reader.readAsDataURL(file);
  };

  const removeReceipt = () => {
    setFormData({
      ...formData,
      receipt: null,
      receipt_filename: ''
    });
    if (receiptInputRef.current) {
      receiptInputRef.current.value = '';
    }
  };

  const viewReceipt = (expense) => {
    if (expense.receipt) {
      setViewingReceipt(expense);
      setShowReceiptModal(true);
    }
  };

  const downloadReceipt = (expense) => {
    if (!expense.receipt) return;
    
    const link = document.createElement('a');
    link.href = expense.receipt;
    link.download = expense.receipt_filename || `receipt_${expense.id}`;
    link.click();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Calculate amount from unit_price, quantity, ancillary
    const unitPrice = parseFloat(formData.unit_price) || 0;
    const quantity = parseFloat(formData.quantity) || 1;
    const ancillary = parseFloat(formData.ancillary) || 0;
    const calculatedAmount = (unitPrice * quantity) + ancillary;
    
    try {
      const payload = {
        ...formData,
        unit_price: unitPrice,
        quantity: quantity,
        ancillary: ancillary,
        amount: calculatedAmount > 0 ? calculatedAmount : (parseFloat(formData.amount) || 0),
        tax: parseFloat(formData.tax) || 0,
        co_agent_id: formData.co_agent_id || null,
        vendor: formData.vendor || null,
        payment_date: formData.payment_date || null,
        receipt: formData.receipt,
        receipt_filename: formData.receipt_filename || null,
        accounting_account_id: formData.accounting_account_id || null
      };

      if (editingExpense) {
        await expensesApi.update(editingExpense.id, payload);
        toast.success('Expense updated');
      } else {
        await expensesApi.create(payload);
        toast.success('Expense created');
      }
      setShowModal(false);
      resetForm();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Operation failed');
    }
  };

  // Group expenses by month for agent view
  const getMonthlyExpenses = () => {
    const grouped = {};
    expenses.forEach(expense => {
      // Parse date string - handle both YYYY-MM-DD and MM/DD/YYYY formats
      const dateStr = expense.expense_date || '';
      let year, month;
      
      if (dateStr.includes('/')) {
        // MM/DD/YYYY format
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          month = parts[0];
          year = parts[2];
        }
      } else if (dateStr.includes('-')) {
        // YYYY-MM-DD format
        const parts = dateStr.split('-');
        if (parts.length >= 2) {
          year = parts[0];
          month = parts[1];
        }
      }
      
      if (!year || !month) return; // Skip invalid dates
      
      const monthKey = `${year}-${month.padStart(2, '0')}`;
      const monthName = new Date(parseInt(year), parseInt(month) - 1, 15).toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      
      if (!grouped[monthKey]) {
        grouped[monthKey] = {
          key: monthKey,
          name: monthName,
          expenses: [],
          totalExpenses: 0,
          totalPaid: 0,
          totalOutstanding: 0
        };
      }
      
      grouped[monthKey].expenses.push(expense);
      const total = expense.total_amount || (expense.amount + expense.tax);
      grouped[monthKey].totalExpenses += total;
      
      if (expense.payment_date) {
        grouped[monthKey].totalPaid += total;
      } else {
        grouped[monthKey].totalOutstanding += total;
      }
    });
    
    // Sort by month descending (newest first)
    return Object.values(grouped).sort((a, b) => b.key.localeCompare(a.key));
  };

  // Toggle month expansion
  const toggleMonth = (monthKey) => {
    setExpandedMonths(prev => ({
      ...prev,
      [monthKey]: !prev[monthKey]
    }));
  };

  // Generate monthly PDF statement (for agent - individual statement)
  const generateMonthlyPdf = async (monthKey, monthName) => {
    setGeneratingMonthlyPdf(monthKey);
    const token = getToken();
    
    try {
      // Get first and last day of the month
      const [year, month] = monthKey.split('-');
      const startDate = `${year}-${month}-01`;
      const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
      const endDate = `${year}-${month}-${String(lastDay).padStart(2, '0')}`;
      
      const prefix = businessUnit === 'insurance' ? 'insurance/' : '';
      const response = await axios.get(
        `${API}/api/${prefix}expenses/statement/${user.agent_id}?start_date=${startDate}&end_date=${endDate}`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Expense_Statement_${monthName.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Statement downloaded for ${monthName}`);
    } catch (err) {
      toast.error('Failed to generate statement');
    } finally {
      setGeneratingMonthlyPdf(null);
    }
  };

  // Generate monthly summary PDF (for admin - all agents summary)
  const generateMonthlySummaryPdf = async (monthKey, monthName) => {
    setGeneratingMonthlyPdf(monthKey);
    const token = getToken();
    
    try {
      const [year, month] = monthKey.split('-');
      const prefix = businessUnit === 'insurance' ? 'insurance/' : '';
      const response = await axios.get(
        `${API}/api/${prefix}expenses/monthly-summary?year=${year}&month=${month}`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Expense_Summary_${monthName.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Summary downloaded for ${monthName}`);
    } catch (err) {
      toast.error('Failed to generate summary');
    } finally {
      setGeneratingMonthlyPdf(null);
    }
  };

  // Calculate overall totals
  const getExpenseTotals = () => {
    let totalExpenses = 0;
    let totalPaid = 0;
    let totalOutstanding = 0;
    
    expenses.forEach(expense => {
      const total = expense.total_amount || (expense.amount + expense.tax);
      totalExpenses += total;
      if (expense.payment_date) {
        totalPaid += total;
      } else {
        totalOutstanding += total;
      }
    });
    
    return { totalExpenses, totalPaid, totalOutstanding };
  };

  // Alias for backward compatibility
  const getAgentTotals = getExpenseTotals;

  // Import Functions - Support multiple files
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setImportFiles(files);
    setImportLoading(true);
    
    try {
      let allData = [];
      const token = getToken();
      
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        
        // Check if PDF or Excel
        if (file.name.toLowerCase().endsWith('.pdf')) {
          // Convert PDF to Excel first
          const response = await axios.post(`${API}/api/convert/pdf-to-excel`, formData, {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          });
          
          if (response.data.data && response.data.data.length > 0) {
            // Add source file name to each row for reference
            const dataWithSource = response.data.data.map(row => ({ ...row, _sourceFile: file.name }));
            allData = [...allData, ...dataWithSource];
          }
        } else {
          // Handle Excel/CSV using import/preview endpoint
          const response = await axios.post(`${API}/api/import/preview`, formData, {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          });
          
          if (response.data.preview_data && response.data.preview_data.length > 0) {
            const dataWithSource = response.data.preview_data.map(row => ({ ...row, _sourceFile: file.name }));
            allData = [...allData, ...dataWithSource];
            // Store session ID and total rows for full import
            setImportSessionId(response.data.session_id);
            setImportTotalRows(response.data.total_rows || response.data.preview_data.length);
          }
        }
      }
      
      if (allData.length > 0) {
        setImportData(allData);
        
        // Auto-map columns based on column names
        const columns = Object.keys(allData[0]);
        const autoMapping = {};
        const fieldMappings = {
          'description': ['description', 'desc', 'item', 'expense', 'name', 'transaction description'],
          'unit_price': ['unit_price', 'unitprice', 'price', 'unit price', 'cost'],
          'quantity': ['quantity', 'qty', 'count'],
          'ancillary': ['ancillary', 'ancilliary', 'other', 'additional', 'ancillary amount'],
          'tax': ['tax', 'gst', 'hst', 'vat'],
          'expense_date': ['expense_date', 'date', 'expensedate', 'expense date'],
          'agent_name': ['agent_name', 'agent', 'agentname', 'agent name', 'id1'],
          'vendor': ['vendor', 'supplier', 'payee']
        };
        
        for (const [field, aliases] of Object.entries(fieldMappings)) {
          for (const col of columns) {
            const colLower = col.toLowerCase().trim();
            if (aliases.some(alias => colLower === alias || colLower.includes(alias))) {
              autoMapping[field] = col;
              break;
            }
          }
        }
        
        setColumnMapping(autoMapping);
        setImportStep('map');
        toast.success(`Found ${importTotalRows || allData.length} rows from ${files.length} file(s) (showing preview)`);
      } else {
        toast.error('No data found in uploaded files');
      }
    } catch (err) {
      console.error('Import error:', err);
      toast.error(err.response?.data?.detail || 'Failed to process files');
    } finally {
      setImportLoading(false);
    }
  };

  const getImportColumns = () => {
    if (importData.length === 0) return [];
    return Object.keys(importData[0]);
  };

  // Fuzzy matching function to calculate similarity percentage between two strings
  const calculateMatchPercentage = (str1, str2) => {
    if (!str1 || !str2) return 0;
    
    const s1 = str1.toLowerCase().trim();
    const s2 = str2.toLowerCase().trim();
    
    if (s1 === s2) return 100;
    if (s1.length === 0 || s2.length === 0) return 0;
    
    // Check if one string contains the other
    if (s1.includes(s2) || s2.includes(s1)) {
      const shorter = s1.length < s2.length ? s1 : s2;
      const longer = s1.length < s2.length ? s2 : s1;
      return Math.round((shorter.length / longer.length) * 100);
    }
    
    // Calculate character-level similarity
    const s1Chars = s1.replace(/\s+/g, '').split('');
    const s2Chars = s2.replace(/\s+/g, '').split('');
    
    let matchingChars = 0;
    const s2CharsCopy = [...s2Chars];
    
    for (const char of s1Chars) {
      const idx = s2CharsCopy.indexOf(char);
      if (idx !== -1) {
        matchingChars++;
        s2CharsCopy.splice(idx, 1); // Remove matched char to avoid double counting
      }
    }
    
    // Calculate percentage based on the longer string
    const maxLength = Math.max(s1Chars.length, s2Chars.length);
    return Math.round((matchingChars / maxLength) * 100);
  };

  // Find the best matching agent based on name similarity - returns agent and confidence score
  const findBestMatchingAgent = (inputName) => {
    if (!inputName || agents.length === 0) return { agent: null, score: 0 };
    
    let bestMatch = null;
    let highestScore = 0;
    const MINIMUM_MATCH_THRESHOLD = 50; // At least 50% match required
    
    for (const agent of agents) {
      const score = calculateMatchPercentage(inputName, agent.name);
      
      // Also check against parts of the name (first name, last name)
      const nameParts = agent.name.split(' ');
      let partScore = 0;
      for (const part of nameParts) {
        const ps = calculateMatchPercentage(inputName, part);
        if (ps > partScore) partScore = ps;
      }
      
      // Use the higher score between full name match and partial match
      const finalScore = Math.max(score, partScore);
      
      if (finalScore > highestScore && finalScore >= MINIMUM_MATCH_THRESHOLD) {
        highestScore = finalScore;
        bestMatch = agent;
      }
    }
    
    return { agent: bestMatch, score: highestScore };
  };

  // Generate agent match preview when column mapping changes
  const updateMatchedAgentsPreview = () => {
    if (!columnMapping.agent_name || importData.length === 0) {
      setMatchedAgentsPreview({});
      return;
    }
    
    const preview = {};
    importData.forEach((row, idx) => {
      const agentName = row[columnMapping.agent_name] || row.agent_name || row.Agent || row.agent || row.Name || row.name;
      if (agentName) {
        const { agent, score } = findBestMatchingAgent(agentName);
        preview[idx] = { 
          inputName: agentName, 
          matchedAgent: agent?.name || 'No match', 
          confidence: score,
          agentId: agent?.id || null
        };
      }
    });
    setMatchedAgentsPreview(preview);
  };

  // Update preview when mapping changes
  useEffect(() => {
    updateMatchedAgentsPreview();
  }, [columnMapping.agent_name, importData]);

  const handleImport = async () => {
    if (importData.length === 0) {
      toast.error('No data to import');
      return;
    }
    
    setImportLoading(true);
    
    try {
      // Use the session-based backend import for full file processing
      if (importSessionId) {
        const token = getToken();
        const response = await axios.post(
          `${API}/api/${businessUnit === 'insurance' ? 'insurance/' : ''}expenses/import/execute`,
          {
            session_id: importSessionId,
            column_mappings: columnMapping
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const result = response.data;
        let message = `Imported ${result.imported} expenses`;
        if (result.skipped > 0) {
          message += `, ${result.skipped} skipped`;
        }
        toast.success(message);
        
        if (result.errors && result.errors.length > 0) {
          console.log('Import errors:', result.errors);
        }
      } else {
        // Fallback: process preview data row by row (for PDF imports or when no session)
        let successCount = 0;
        let errorCount = 0;
        let agentMatchedCount = 0;
        
        for (const row of importData) {
          try {
            const unitPrice = parseFloat(row[columnMapping.unit_price] || row.unit_price || row['Unit Price'] || 0);
            const quantity = parseFloat(row[columnMapping.quantity] || row.quantity || row.Quantity || 1);
            const ancillary = parseFloat(row[columnMapping.ancillary] || row.ancillary || row.Ancillary || 0);
            const calculatedAmount = (unitPrice * quantity) + ancillary;
            
            let taxValue = parseFloat(row[columnMapping.tax] || row.tax || row.Tax);
            if (isNaN(taxValue) || taxValue === 0) {
              taxValue = calculatedAmount * 0.05;
            }
            
            const mappedData = {
              agent_id: agents[0]?.id || '',
              description: row[columnMapping.description] || row.description || row.Description || 'Imported Expense',
              unit_price: unitPrice,
              quantity: quantity,
              ancillary: ancillary,
              amount: calculatedAmount,
              tax: taxValue,
              expense_date: row[columnMapping.expense_date] || row.expense_date || row.Date || new Date().toISOString().split('T')[0],
            };
            
            const agentName = row[columnMapping.agent_name] || row.agent_name || row.Agent || row.agent || row.Name || row.name || row.ID1;
            if (agentName) {
              const { agent } = findBestMatchingAgent(agentName);
              if (agent) {
                mappedData.agent_id = agent.id;
                agentMatchedCount++;
              }
            }
            
            await expensesApi.create(mappedData);
            successCount++;
          } catch (err) {
            errorCount++;
          }
        }
        
        let message = `Imported ${successCount} expenses`;
        if (agentMatchedCount > 0) {
          message += ` (${agentMatchedCount} auto-matched to agents)`;
        }
        if (errorCount > 0) {
          message += `, ${errorCount} failed`;
        }
        toast.success(message);
      }
      
      // Reset state
      setShowImportModal(false);
      setImportStep('upload');
      setImportData([]);
      setImportFiles([]);
      setColumnMapping({});
      setMatchedAgentsPreview({});
      setImportSessionId(null);
      setImportTotalRows(0);
      loadData();
    } catch (err) {
      console.error('Import error:', err);
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImportLoading(false);
    }
  };

  // Recalculate 5% tax for all existing expenses
  const recalculateTax = async () => {
    if (!window.confirm('This will recalculate 5% tax for ALL existing expenses. Continue?')) return;
    
    const token = getToken();
    try {
      const response = await axios.post(
        `${API}/api/${businessUnit === 'insurance' ? 'insurance/' : ''}expenses/recalculate-tax`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      loadData(); // Reload to show updated values
    } catch (err) {
      toast.error('Failed to recalculate tax');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await expensesApi.delete(id);
      toast.success('Expense deleted');
      loadData();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  // Bulk delete function
  const handleBulkDelete = async () => {
    if (selectedExpenses.length === 0) {
      toast.error('No expenses selected');
      return;
    }
    
    if (!window.confirm(`Delete ${selectedExpenses.length} selected expense(s)?`)) return;
    
    let successCount = 0;
    let errorCount = 0;
    
    for (const id of selectedExpenses) {
      try {
        await expensesApi.delete(id);
        successCount++;
      } catch (err) {
        errorCount++;
      }
    }
    
    if (successCount > 0) {
      toast.success(`Deleted ${successCount} expense(s)${errorCount > 0 ? `, ${errorCount} failed` : ''}`);
      setSelectedExpenses([]);
      setSelectAll(false);
      loadData();
    } else {
      toast.error('Failed to delete expenses');
    }
  };

  // Filtered expenses - filter by search only (approval removed)
  const filteredExpenses = expenses.filter(e => {
    const matchesSearch = e.agent_name?.toLowerCase().includes(search.toLowerCase()) ||
                          e.description?.toLowerCase().includes(search.toLowerCase()) ||
                          e.vendor?.toLowerCase().includes(search.toLowerCase());
    return matchesSearch;
  });

  const totalExpenses = filteredExpenses.reduce((sum, e) => sum + e.total_amount, 0);

  // Toggle single expense selection
  const toggleExpenseSelection = (id) => {
    setSelectedExpenses(prev => 
      prev.includes(id) 
        ? prev.filter(expId => expId !== id)
        : [...prev, id]
    );
  };

  // Toggle select all
  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedExpenses([]);
    } else {
      setSelectedExpenses(filteredExpenses.map(e => e.id));
    }
    setSelectAll(!selectAll);
  };

  // Generate expense statement PDF
  const generateExpenseStatement = async (download = true) => {
    if (!statementForm.agent_id) {
      toast.error('Please select an agent');
      return;
    }
    
    setGeneratingStatement(true);
    const token = getToken();
    
    try {
      const params = new URLSearchParams();
      if (statementForm.start_date) params.append('start_date', statementForm.start_date);
      if (statementForm.end_date) params.append('end_date', statementForm.end_date);
      
      const prefix = businessUnit === 'insurance' ? 'insurance/' : '';
      const response = await axios.get(
        `${API}/api/${prefix}expenses/statement/${statementForm.agent_id}?${params.toString()}`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const agent = agents.find(a => a.id === statementForm.agent_id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Expense_Statement_${agent?.name?.replace(/\s+/g, '_') || 'Agent'}_${statementForm.start_date || 'all'}_${statementForm.end_date || 'time'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Expense statement downloaded');
    } catch (err) {
      toast.error('Failed to generate statement');
    } finally {
      setGeneratingStatement(false);
    }
  };

  // Email expense statement to agent
  const emailExpenseStatement = async () => {
    if (!statementForm.agent_id) {
      toast.error('Please select an agent');
      return;
    }
    
    setGeneratingStatement(true);
    const token = getToken();
    
    try {
      const params = new URLSearchParams();
      if (statementForm.start_date) params.append('start_date', statementForm.start_date);
      if (statementForm.end_date) params.append('end_date', statementForm.end_date);
      
      const prefix = businessUnit === 'insurance' ? 'insurance/' : '';
      const response = await axios.post(
        `${API}/api/${prefix}expenses/statement/${statementForm.agent_id}/email?${params.toString()}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message || 'Expense statement emailed to agent');
      setShowStatementModal(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to email statement');
    } finally {
      setGeneratingStatement(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="expenses-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
            {isAgent ? 'My Expenses' : 'Expenses'}
          </h1>
          <p className="text-slate-500 mt-1">
            {isAgent ? 'View your expenses and deductions' : 'Track agent expenses and deductions'}
          </p>
        </div>
        {!isAgent && (
          <div className="flex gap-2 flex-wrap items-center">
            <Button
              variant="outline"
              size="sm"
              onClick={toggleSelectAll}
              data-testid="select-all-expenses-btn"
              className="border-slate-300"
            >
              <Checkbox
                checked={selectAll}
                className="mr-2"
              />
              {selectAll ? 'Deselect All' : 'Select All'}
            </Button>
            {selectedExpenses.length > 0 && (
              <Button
                variant="destructive"
                onClick={handleBulkDelete}
                data-testid="bulk-delete-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Delete ({selectedExpenses.length})
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => handleExport('csv')}
              disabled={exportLoading}
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" /> CSV
            </Button>
            <Button
              variant="outline"
              onClick={() => handleExport('excel')}
              disabled={exportLoading}
              data-testid="export-excel-btn"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
            </Button>
            <Button
              variant="outline"
              onClick={handleBulkCategorize}
              disabled={bulkCategorizingLoading}
              className="border-purple-300 text-purple-700 hover:bg-purple-50"
              data-testid="bulk-categorize-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${bulkCategorizingLoading ? 'animate-spin' : ''}`} /> 
              {bulkCategorizingLoading ? 'Categorizing...' : 'Auto-Categorize'}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowStatementModal(true)}
              data-testid="generate-statement-btn"
            >
              <FileOutput className="w-4 h-4 mr-2" /> Statement
            </Button>
            <Button
              variant="outline"
              onClick={() => { setImportStep('upload'); setImportFiles([]); setShowImportModal(true); }}
              data-testid="import-expenses-btn"
            >
              <Upload className="w-4 h-4 mr-2" /> Import
            </Button>
            <Button
              onClick={() => { resetForm(); setShowModal(true); }}
              data-testid="add-expense-btn"
              className="bg-slate-900 hover:bg-slate-800"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Expense
            </Button>
          </div>
        )}
      </div>

      {/* Stats & Search */}
      <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
        {/* Stats cards for both Admin and Agent */}
        <Card className="flex-shrink-0 border-slate-100">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center">
              <Receipt className="w-6 h-6 text-amber-500" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Total Expenses</p>
              <p className="text-xl font-bold font-mono text-slate-900">{formatCurrency(getExpenseTotals().totalExpenses)}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="flex-shrink-0 border-emerald-100 bg-emerald-50/30">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
              <CheckSquare className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-xs text-emerald-600 uppercase tracking-wider">Paid Expenses</p>
              <p className="text-xl font-bold font-mono text-emerald-700">{formatCurrency(getExpenseTotals().totalPaid)}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="flex-shrink-0 border-red-100 bg-red-50/30">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
              <Calendar className="w-6 h-6 text-red-500" />
            </div>
            <div>
              <p className="text-xs text-red-600 uppercase tracking-wider">Outstanding</p>
              <p className="text-xl font-bold font-mono text-red-700">{formatCurrency(getExpenseTotals().totalOutstanding)}</p>
            </div>
          </CardContent>
        </Card>
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search expenses by agent, description, or vendor..."
            data-testid="search-expenses"
            className="pl-10"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Monthly Summary View - For both Admin and Agent */}
      <div className="space-y-3" data-testid="monthly-summary-section">
        <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
          <CalendarRange className="w-5 h-5" />
          Monthly Summary
        </h2>
        {getMonthlyExpenses().length === 0 ? (
          <Card className="border-slate-100">
            <CardContent className="py-8 text-center text-slate-500">
              No expenses found
            </CardContent>
          </Card>
        ) : (
          getMonthlyExpenses().map(month => (
            <Card key={month.key} className="border-slate-100 overflow-hidden" data-testid={`month-card-${month.key}`}>
              {/* Month Header - Clickable */}
              <div
                className="flex items-center justify-between p-4 bg-slate-50/70 cursor-pointer hover:bg-slate-100/70 transition-colors"
                onClick={() => toggleMonth(month.key)}
                data-testid={`month-header-${month.key}`}
              >
                <div className="flex items-center gap-3">
                  {expandedMonths[month.key] ? (
                    <ChevronDown className="w-5 h-5 text-slate-500" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-slate-500" />
                  )}
                  <div>
                    <h3 className="font-semibold text-slate-800">{month.name}</h3>
                    <p className="text-sm text-slate-500">{month.expenses.length} expense{month.expenses.length !== 1 ? 's' : ''}</p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <p className="text-xs text-slate-500 uppercase">Total</p>
                    <p className="font-mono font-bold text-slate-900">{formatCurrency(month.totalExpenses)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-emerald-600 uppercase">Paid</p>
                    <p className="font-mono font-bold text-emerald-600">{formatCurrency(month.totalPaid)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-red-500 uppercase">Outstanding</p>
                    <p className="font-mono font-bold text-red-500">{formatCurrency(month.totalOutstanding)}</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isAgent) {
                        generateMonthlyPdf(month.key, month.name);
                      } else {
                        generateMonthlySummaryPdf(month.key, month.name);
                      }
                    }}
                    disabled={generatingMonthlyPdf === month.key}
                    data-testid={`download-monthly-pdf-${month.key}`}
                    className="ml-2"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    {generatingMonthlyPdf === month.key ? 'Generating...' : 'PDF'}
                  </Button>
                </div>
              </div>
              
              {/* Expanded Content - Expense Details */}
              {expandedMonths[month.key] && (
                <div className="border-t border-slate-200">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-white">
                        {!isAgent && (
                          <TableHead className="w-10">
                            <Checkbox
                              checked={month.expenses.every(e => selectedExpenses.includes(e.id)) && month.expenses.length > 0}
                              onCheckedChange={() => {
                                const monthExpenseIds = month.expenses.map(e => e.id);
                                const allSelected = monthExpenseIds.every(id => selectedExpenses.includes(id));
                                if (allSelected) {
                                  setSelectedExpenses(prev => prev.filter(id => !monthExpenseIds.includes(id)));
                                } else {
                                  setSelectedExpenses(prev => [...new Set([...prev, ...monthExpenseIds])]);
                                }
                              }}
                              data-testid={`select-all-${month.key}`}
                            />
                          </TableHead>
                        )}
                        <TableHead>Date</TableHead>
                        {!isAgent && <TableHead>Agent</TableHead>}
                        <TableHead>Vendor</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="text-right">Tax</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="text-center">Payment</TableHead>
                        <TableHead className="text-center">Receipt</TableHead>
                        {!isAgent && <TableHead className="text-right">Actions</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {month.expenses.map(expense => (
                        <TableRow key={expense.id} className="hover:bg-slate-50">
                          {!isAgent && (
                            <TableCell>
                              <Checkbox
                                checked={selectedExpenses.includes(expense.id)}
                                onCheckedChange={() => toggleExpenseSelection(expense.id)}
                              />
                            </TableCell>
                          )}
                          <TableCell className="font-mono text-sm">{formatDate(expense.expense_date)}</TableCell>
                          {!isAgent && (
                            <TableCell>
                              <p className="font-medium text-slate-900">{expense.agent_name}</p>
                            </TableCell>
                          )}
                          <TableCell>
                            {expense.vendor ? (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button 
                                    className="text-left text-slate-700 hover:text-blue-600 hover:underline cursor-pointer"
                                    onClick={() => fetchVendorStats(expense.vendor)}
                                  >
                                    {expense.vendor}
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-72 p-0" align="start">
                                  <div className="p-4 border-b bg-slate-50">
                                    <div className="flex items-center gap-2">
                                      <Building2 className="w-5 h-5 text-slate-600" />
                                      <h4 className="font-semibold text-slate-900">{expense.vendor}</h4>
                                    </div>
                                    {vendorStats[expense.vendor]?.is_registered && (
                                      <Badge variant="outline" className="mt-2 text-xs">Registered Vendor</Badge>
                                    )}
                                  </div>
                                  <div className="p-4 space-y-3">
                                    {loadingVendorStats[expense.vendor] ? (
                                      <div className="text-center py-2 text-slate-500 text-sm">Loading...</div>
                                    ) : vendorStats[expense.vendor] ? (
                                      <>
                                        {vendorStats[expense.vendor].email && (
                                          <div className="flex items-center gap-2 text-sm">
                                            <Mail className="w-4 h-4 text-slate-400" />
                                            <span className="text-slate-600">{vendorStats[expense.vendor].email}</span>
                                          </div>
                                        )}
                                        {vendorStats[expense.vendor].phone && (
                                          <div className="flex items-center gap-2 text-sm">
                                            <Phone className="w-4 h-4 text-slate-400" />
                                            <span className="text-slate-600">{vendorStats[expense.vendor].phone}</span>
                                          </div>
                                        )}
                                        <div className="pt-2 border-t">
                                          <div className="flex items-center justify-between text-sm">
                                            <span className="text-slate-500">Total Spent:</span>
                                            <span className="font-semibold text-slate-900">{formatCurrency(vendorStats[expense.vendor].total_spent || 0)}</span>
                                          </div>
                                          <div className="flex items-center justify-between text-sm mt-1">
                                            <span className="text-slate-500">Transactions:</span>
                                            <span className="font-medium text-slate-700">{vendorStats[expense.vendor].expense_count || vendorStats[expense.vendor].transaction_count || 0}</span>
                                          </div>
                                          {vendorStats[expense.vendor].payment_terms && (
                                            <div className="flex items-center justify-between text-sm mt-1">
                                              <span className="text-slate-500">Terms:</span>
                                              <span className="font-medium text-slate-700">{vendorStats[expense.vendor].payment_terms?.replace('net', 'Net ')}</span>
                                            </div>
                                          )}
                                        </div>
                                      </>
                                    ) : (
                                      <div className="text-center py-2 text-slate-500 text-sm">Click to load vendor info</div>
                                    )}
                                  </div>
                                </PopoverContent>
                              </Popover>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate">{expense.description}</TableCell>
                          <TableCell>
                            {expense.accounting_account_name ? (
                              <Badge variant="outline" className="text-xs whitespace-nowrap">
                                {expense.accounting_account_name}
                              </Badge>
                            ) : (
                              <span className="text-xs text-slate-400">Uncategorized</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-mono">{formatCurrency(expense.amount)}</TableCell>
                          <TableCell className="text-right font-mono text-slate-500">{formatCurrency(expense.tax)}</TableCell>
                          <TableCell className="text-right font-mono font-semibold">{formatCurrency(expense.total_amount || (expense.amount + expense.tax))}</TableCell>
                          <TableCell className="text-center">
                            {expense.payment_date ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                                Paid {formatDate(expense.payment_date)}
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                                Outstanding
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {expense.receipt ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => { setViewingReceipt(expense.receipt); setShowReceiptModal(true); }}
                              >
                                <Eye className="w-4 h-4 text-blue-500" />
                              </Button>
                            ) : (
                              <span className="text-slate-400">—</span>
                            )}
                          </TableCell>
                          {!isAgent && (
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEdit(expense)}
                                  className="text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDelete(expense.id)}
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </Card>
          ))
        )}
      </div>

      {/* Agent-only original monthly view - REMOVED, now unified above */}

      {/* Modal - Admin Only */}
      {!isAgent && (
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent data-testid="expense-modal">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope' }}>
                {editingExpense ? 'Edit Expense' : 'Add Expense'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Agent *</Label>
              <Select
                value={formData.agent_id}
                onValueChange={(v) => setFormData({ ...formData, agent_id: v })}
              >
                <SelectTrigger data-testid="expense-agent-select">
                  <SelectValue placeholder="Select agent" />
                </SelectTrigger>
                <SelectContent>
                  {agents.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Description *</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Marketing expense, office supplies, etc."
                required
                data-testid="expense-description-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Vendor</Label>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Select
                    value={vendors.find(v => v.name === formData.vendor)?.id || "manual"}
                    onValueChange={handleVendorSelect}
                  >
                    <SelectTrigger data-testid="expense-vendor-select" className="flex-1">
                      <SelectValue placeholder="Select vendor or enter manually" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">-- Enter manually --</SelectItem>
                      {vendors.map((v) => (
                        <SelectItem key={v.id} value={v.id}>
                          {v.name}
                          {v.default_expense_category_name && (
                            <span className="text-slate-400 ml-2">({v.default_expense_category_name})</span>
                          )}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={openQuickAddVendor}
                    className="shrink-0"
                    data-testid="quick-add-vendor-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" /> New
                  </Button>
                </div>
                {(!vendors.find(v => v.name === formData.vendor) || formData.vendor === '') && (
                  <Input
                    value={formData.vendor}
                    onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                    placeholder="Enter vendor name manually"
                    data-testid="expense-vendor-input"
                  />
                )}
              </div>
              {vendors.length > 0 && (
                <p className="text-xs text-slate-500">
                  Select a vendor to auto-fill category and calculate due date
                </p>
              )}
            </div>

            {/* Quick Add Vendor Inline Form */}
            {showQuickAddVendor && (
              <div className="border rounded-lg p-4 bg-blue-50/50 space-y-3">
                <div className="flex justify-between items-center">
                  <Label className="text-sm font-semibold text-blue-800">Quick Add Vendor</Label>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setShowQuickAddVendor(false)}
                    className="h-6 w-6 p-0"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Label className="text-xs">Vendor Name *</Label>
                    <Input
                      value={quickVendorForm.name}
                      onChange={(e) => setQuickVendorForm({ ...quickVendorForm, name: e.target.value })}
                      placeholder="Company or individual name"
                      className="h-8"
                      data-testid="quick-vendor-name"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Email</Label>
                    <Input
                      type="email"
                      value={quickVendorForm.email}
                      onChange={(e) => setQuickVendorForm({ ...quickVendorForm, email: e.target.value })}
                      placeholder="vendor@email.com"
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Phone</Label>
                    <Input
                      value={quickVendorForm.phone}
                      onChange={(e) => setQuickVendorForm({ ...quickVendorForm, phone: e.target.value })}
                      placeholder="(204) 555-0123"
                      className="h-8"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Vendor Type</Label>
                    <Select 
                      value={quickVendorForm.vendor_type} 
                      onValueChange={(v) => setQuickVendorForm({ ...quickVendorForm, vendor_type: v })}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="supplier">Supplier</SelectItem>
                        <SelectItem value="contractor">Contractor</SelectItem>
                        <SelectItem value="service_provider">Service Provider</SelectItem>
                        <SelectItem value="utility">Utility</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Payment Terms</Label>
                    <Select 
                      value={quickVendorForm.payment_terms} 
                      onValueChange={(v) => setQuickVendorForm({ ...quickVendorForm, payment_terms: v })}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="immediate">Due on Receipt</SelectItem>
                        <SelectItem value="net15">Net 15</SelectItem>
                        <SelectItem value="net30">Net 30</SelectItem>
                        <SelectItem value="net45">Net 45</SelectItem>
                        <SelectItem value="net60">Net 60</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Default Category</Label>
                    <Select 
                      value={quickVendorForm.default_expense_category || "none"} 
                      onValueChange={(v) => setQuickVendorForm({ ...quickVendorForm, default_expense_category: v === "none" ? "" : v })}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue placeholder="No default" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No default category</SelectItem>
                        {accountingCategories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.account_number} - {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setShowQuickAddVendor(false)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="button" 
                    size="sm" 
                    onClick={saveQuickVendor}
                    disabled={savingQuickVendor}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="save-quick-vendor-btn"
                  >
                    {savingQuickVendor ? 'Saving...' : 'Add & Select Vendor'}
                  </Button>
                </div>
              </div>
            )}

            {/* Accounting Category */}
            <div className="space-y-2">
              <Label>Accounting Category</Label>
              <Select
                value={formData.accounting_account_id || "auto"}
                onValueChange={(v) => setFormData({ ...formData, accounting_account_id: v === "auto" ? "" : v })}
              >
                <SelectTrigger data-testid="expense-category-select">
                  <SelectValue placeholder="Auto-categorize (leave empty)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto-categorize</SelectItem>
                  {accountingCategories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.account_number} - {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                Leave empty to auto-categorize based on description/vendor
              </p>
            </div>

            {/* Unit Price, Quantity, Ancillary */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Unit Price</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.unit_price}
                  onChange={(e) => handlePriceChange('unit_price', e.target.value)}
                  placeholder="0.00"
                  data-testid="expense-unit-price-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Quantity</Label>
                <Input
                  type="number"
                  step="1"
                  value={formData.quantity}
                  onChange={(e) => handlePriceChange('quantity', e.target.value)}
                  placeholder="1"
                  data-testid="expense-quantity-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Ancillary</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.ancillary}
                  onChange={(e) => handlePriceChange('ancillary', e.target.value)}
                  placeholder="0.00"
                  data-testid="expense-ancillary-input"
                />
              </div>
            </div>

            {/* Calculated Amount Display */}
            <div className="p-3 bg-slate-50 rounded-lg border">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Calculated Amount:</span>
                <span className="font-mono font-medium">
                  {formatCurrency((parseFloat(formData.unit_price) || 0) * (parseFloat(formData.quantity) || 1) + (parseFloat(formData.ancillary) || 0))}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tax (Default 5%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.tax}
                  onChange={(e) => setFormData({ ...formData, tax: e.target.value })}
                  placeholder="5.00"
                  data-testid="expense-tax-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Total</Label>
                <div className="h-10 px-3 flex items-center bg-amber-50 rounded-md border border-amber-200">
                  <span className="font-mono font-medium text-amber-700">
                    {formatCurrency(
                      ((parseFloat(formData.unit_price) || 0) * (parseFloat(formData.quantity) || 1) + (parseFloat(formData.ancillary) || 0)) + 
                      (parseFloat(formData.tax) || 0)
                    )}
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Expense Date *</Label>
                <Input
                  type="date"
                  value={formData.expense_date}
                  onChange={(e) => setFormData({ ...formData, expense_date: e.target.value })}
                  data-testid="expense-date-input"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Payment Date</Label>
                <Input
                  type="date"
                  value={formData.payment_date}
                  onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
                  data-testid="payment-date-input"
                />
                <p className="text-xs text-slate-500">Leave empty if not yet paid</p>
              </div>
            </div>

            {/* Receipt Upload Section */}
            <div className="space-y-2">
              <Label>Receipt (Optional)</Label>
              {formData.receipt ? (
                <div className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <FileImage className="w-5 h-5 text-emerald-600" />
                  <span className="text-sm text-emerald-800 flex-1 truncate">
                    {formData.receipt_filename || 'Receipt uploaded'}
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={removeReceipt}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div
                  className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center cursor-pointer hover:border-slate-400 hover:bg-slate-50 transition-colors"
                  onClick={() => receiptInputRef.current?.click()}
                >
                  <input
                    ref={receiptInputRef}
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={handleReceiptUpload}
                    className="hidden"
                    data-testid="receipt-file-input"
                  />
                  <Upload className="w-6 h-6 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm text-slate-600">Click to upload receipt</p>
                  <p className="text-xs text-slate-400 mt-1">JPG, PNG, GIF, WebP or PDF (max 5MB)</p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-expense-btn">
                {editingExpense ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      )}

      {/* Receipt View Modal */}
      <Dialog open={showReceiptModal} onOpenChange={setShowReceiptModal}>
        <DialogContent className="max-w-2xl max-h-[90vh]" data-testid="receipt-modal">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              Receipt - {viewingReceipt?.description || 'Expense'}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {viewingReceipt?.receipt && (
              viewingReceipt.receipt.startsWith('data:application/pdf') ? (
                <div className="text-center py-8">
                  <FileImage className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                  <p className="text-slate-600 mb-4">PDF Receipt: {viewingReceipt.receipt_filename}</p>
                  <Button onClick={() => downloadReceipt(viewingReceipt)}>
                    <Download className="w-4 h-4 mr-2" /> Download PDF
                  </Button>
                </div>
              ) : (
                <img 
                  src={viewingReceipt.receipt} 
                  alt="Receipt" 
                  className="max-w-full max-h-[60vh] mx-auto rounded-lg border"
                />
              )
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowReceiptModal(false)}>
              Close
            </Button>
            <Button onClick={() => downloadReceipt(viewingReceipt)}>
              <Download className="w-4 h-4 mr-2" /> Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="import-expense-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <FileSpreadsheet className="w-5 h-5" />
              Import Expenses
            </DialogTitle>
          </DialogHeader>
          
          <Tabs value={importStep} className="mt-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="upload" disabled>1. Upload Files</TabsTrigger>
              <TabsTrigger value="map" disabled>2. Map & Import</TabsTrigger>
            </TabsList>
            
            <TabsContent value="upload" className="mt-4">
              <div className="space-y-4">
                <div
                  className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center cursor-pointer hover:border-slate-400 transition-colors"
                  onClick={() => document.getElementById('expense-import-file')?.click()}
                >
                  <input
                    id="expense-import-file"
                    type="file"
                    accept=".xlsx,.xls,.csv,.pdf"
                    multiple
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <p className="text-lg font-medium text-slate-700">Click to upload files</p>
                  <p className="text-sm text-slate-500 mt-2">Supports multiple Excel (.xlsx, .xls), CSV, and PDF files</p>
                  <p className="text-xs text-blue-600 mt-1">Hold Ctrl/Cmd to select multiple files</p>
                </div>
                
                {importFiles.length > 0 && (
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm font-medium text-blue-800 mb-2">Selected Files ({importFiles.length}):</p>
                    <ul className="text-sm text-blue-700 space-y-1">
                      {importFiles.map((f, i) => (
                        <li key={i} className="flex items-center gap-2">
                          <FileSpreadsheet className="w-4 h-4" /> {f.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {importLoading && (
                  <div className="flex items-center justify-center gap-2 text-slate-600">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-600"></div>
                    Processing files...
                  </div>
                )}
              </div>
            </TabsContent>
            
            <TabsContent value="map" className="mt-4">
              <div className="space-y-4">
                <p className="text-sm text-slate-600">
                  Found <strong>{importTotalRows || importData.length}</strong> rows from {importFiles.length} file(s). 
                  {importTotalRows > importData.length && <span className="text-blue-600"> (showing {importData.length} preview rows)</span>}
                  {' '}Map the columns to expense fields:
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  {['description', 'unit_price', 'quantity', 'ancillary', 'tax', 'expense_date', 'agent_name', 'vendor'].map(field => (
                    <div key={field} className="space-y-1">
                      <Label className="text-xs capitalize">{field.replace('_', ' ')}{field === 'tax' ? ' (Default 5%)' : ''}</Label>
                      <Select
                        value={columnMapping[field] || '_skip_'}
                        onValueChange={(v) => setColumnMapping({ ...columnMapping, [field]: v === '_skip_' ? undefined : v })}
                      >
                        <SelectTrigger className="h-8 text-sm">
                          <SelectValue placeholder="Select column" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_skip_">-- Skip --</SelectItem>
                          {getImportColumns().filter(c => c !== '_sourceFile').map(col => (
                            <SelectItem key={col} value={col}>{col}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
                
                {/* Agent Match Preview with Confidence */}
                {Object.keys(matchedAgentsPreview).length > 0 && (
                  <div className="mt-4 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                    <p className="text-sm font-medium text-emerald-800 mb-2">Agent Match Preview:</p>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {Object.entries(matchedAgentsPreview).slice(0, 5).map(([idx, match]) => (
                        <div key={idx} className="flex items-center justify-between text-xs">
                          <span className="text-slate-600">&quot;{match.inputName}&quot;</span>
                          <span className="flex items-center gap-2">
                            <span className={`font-medium ${match.confidence >= 80 ? 'text-emerald-600' : match.confidence >= 50 ? 'text-amber-600' : 'text-red-500'}`}>
                              → {match.matchedAgent}
                            </span>
                            <span className={`px-2 py-0.5 rounded text-xs ${match.confidence >= 80 ? 'bg-emerald-100 text-emerald-700' : match.confidence >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                              {match.confidence}%
                            </span>
                          </span>
                        </div>
                      ))}
                      {Object.keys(matchedAgentsPreview).length > 5 && (
                        <p className="text-xs text-slate-500 italic">...and {Object.keys(matchedAgentsPreview).length - 5} more</p>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Preview */}
                {importData.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-medium mb-2">Preview (first 3 rows):</p>
                    <div className="overflow-x-auto">
                      <Table className="text-xs">
                        <TableHeader>
                          <TableRow>
                            {getImportColumns().filter(c => c !== '_sourceFile').slice(0, 6).map(col => (
                              <TableHead key={col} className="py-1">{col}</TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {importData.slice(0, 3).map((row, idx) => (
                            <TableRow key={idx}>
                              {getImportColumns().filter(c => c !== '_sourceFile').slice(0, 6).map(col => (
                                <TableCell key={col} className="py-1">{String(row[col] || '').substring(0, 20)}</TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => {
              setShowImportModal(false);
              setImportStep('upload');
              setImportData([]);
              setImportFiles([]);
              setColumnMapping({});
              setMatchedAgentsPreview({});
              setImportSessionId(null);
              setImportTotalRows(0);
            }}>
              Cancel
            </Button>
            {importStep === 'map' && (
              <Button onClick={handleImport} disabled={importLoading}>
                <ArrowRight className="w-4 h-4 mr-2" />
                {importLoading ? 'Importing...' : `Import ${importTotalRows || importData.length} Expenses`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Expense Statement Modal */}
      <Dialog open={showStatementModal} onOpenChange={setShowStatementModal}>
        <DialogContent data-testid="statement-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <FileOutput className="w-5 h-5" />
              Generate Expense Statement
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Agent *</Label>
              <Select
                value={statementForm.agent_id}
                onValueChange={(v) => setStatementForm({ ...statementForm, agent_id: v })}
              >
                <SelectTrigger data-testid="statement-agent-select">
                  <SelectValue placeholder="Select agent" />
                </SelectTrigger>
                <SelectContent>
                  {agents.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={statementForm.start_date}
                  onChange={(e) => setStatementForm({ ...statementForm, start_date: e.target.value })}
                  data-testid="statement-start-date"
                />
                <p className="text-xs text-slate-500">Leave empty for all time</p>
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={statementForm.end_date}
                  onChange={(e) => setStatementForm({ ...statementForm, end_date: e.target.value })}
                  data-testid="statement-end-date"
                />
              </div>
            </div>
            
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> This will generate a PDF statement with all expenses for the selected agent within the date range. 
                You can download the PDF or email it directly to the agent.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatementModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="outline"
              onClick={emailExpenseStatement}
              disabled={generatingStatement || !statementForm.agent_id}
              data-testid="email-statement-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              {generatingStatement ? 'Sending...' : 'Email to Agent'}
            </Button>
            <Button 
              onClick={() => generateExpenseStatement(true)}
              disabled={generatingStatement || !statementForm.agent_id}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="download-statement-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              {generatingStatement ? 'Generating...' : 'Download PDF'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
